import React from 'react';
import { Descriptions, Spin, Typography, Tag } from 'antd';
import Drawer from '../../../components/AppDrawer';
import type { OfferingDetail } from '../types';
import AuditProperties from '../../../components/AuditProperties';

const { Text } = Typography;

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return s ?? '';
  }
}

function formatAmount(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export interface OfferingViewDrawerProps {
  open: boolean;
  onClose: () => void;
  offering: OfferingDetail | null | undefined;
  loading: boolean;
  onEdit: () => void;
}

const OfferingViewDrawer: React.FC<OfferingViewDrawerProps> = ({
  open,
  onClose,
  offering,
  loading,
  onEdit,
}) => {

  const getDiscountStatus = () => {
    if (!offering) return 'none';
    const now = new Date().getTime();
    const hasDiscount = offering.discountValue && offering.discountValue > 0;
    if (!hasDiscount) return 'none';

    if (offering.discountStart && new Date(offering.discountStart).getTime() > now) return 'upcoming';
    if (offering.discountEnd && new Date(offering.discountEnd).getTime() < now) return 'expired';

    return 'active';
  };

  const getEffectivePrice = () => {
    if (!offering) return null;
    const now = new Date().getTime();
    const hasDiscount = offering.discountValue && offering.discountValue > 0;
    if (!hasDiscount) return offering.basePrice;

    if (offering.discountStart && new Date(offering.discountStart).getTime() > now) return offering.basePrice;
    if (offering.discountEnd && new Date(offering.discountEnd).getTime() < now) return offering.basePrice;

    if (offering.discountType === 1) { // Percent
      return offering.basePrice * (1 - offering.discountValue! / 100);
    }

    return Math.max(0, offering.basePrice - offering.discountValue!);
  };

  const discountStatus = getDiscountStatus();
  const effectivePrice = getEffectivePrice();

  return (
    <Drawer
      title="Offering details"
      placement="right"
      size="large"
      open={open}
      onClose={onClose}
      extra={
        <Typography.Link onClick={onEdit} style={{ marginRight: 8 }}>
          Edit
        </Typography.Link>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : offering == null ? (
        <Text type="secondary">Offering not found.</Text>
      ) : (
        <>
          <div className="cp-form-section">
            <Descriptions column={2} size="small" title="Basic information">
              <Descriptions.Item label="Name" span={2}>
                <Text strong>{offering.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {offering.status === 0 ? <Tag color="green">Active</Tag> : <Tag color="default">Deactivated</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {offering.offeringCategoryTitle ?? '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>

          <div className="cp-form-section">
            <Descriptions column={2} size="small" title="Pricing & Discount">
              <Descriptions.Item label="Base price">
                {formatAmount(offering.basePrice)}
              </Descriptions.Item>
              <Descriptions.Item label="Effective price">
                {discountStatus === 'active' ? (
                  <Text type="success" strong>{formatAmount(effectivePrice!)}</Text>
                ) : discountStatus === 'upcoming' ? (
                  <Text type="secondary">{formatAmount(offering.basePrice)} (discount starts {formatDate(offering.discountStart)})</Text>
                ) : discountStatus === 'expired' ? (
                  <Text type="secondary">{formatAmount(offering.basePrice)} (discount ended)</Text>
                ) : (
                  <Text type="secondary">{formatAmount(offering.basePrice)}</Text>
                )}
              </Descriptions.Item>
              
              <Descriptions.Item label="Discount status">
                {discountStatus === 'active' ? <Tag color="green">Active</Tag> :
                 discountStatus === 'upcoming' ? <Tag color="blue">Scheduled</Tag> :
                 discountStatus === 'expired' ? <Tag color="default">Expired</Tag> : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Discount type">
                {offering.discountType === 0 ? 'Flat Amount' : offering.discountType === 1 ? 'Percentage' : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Discount value">
                {offering.discountValue
                  ? (offering.discountType === 1 ? `${offering.discountValue}%` : formatAmount(offering.discountValue))
                  : '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Discount start">
                {formatDate(offering.discountStart) || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Discount end">
                {formatDate(offering.discountEnd) || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>

          {offering.description && (
            <div className="cp-form-section">
              <Text strong>Description</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{offering.description}</Text>
              </div>
            </div>
          )}

          <AuditProperties
            className="cp-form-section"
            values={{
              createdAtUtc: offering.createdAtUtc,
              createdByDisplayName: offering.createdByDisplayName,
              createdByUserId: offering.createdByUserId,
              lastModifiedAtUtc: offering.lastModifiedAtUtc,
              modifiedByDisplayName: offering.modifiedByDisplayName,
              modifiedByUserId: offering.modifiedByUserId,
            }}
          />
        </>
      )}
    </Drawer>
  );
};

export default OfferingViewDrawer;
